/**
 * Column policy and localStorage cache-key resolution for the Inspections listing.
 *
 * Rules from the contract:
 *  - allowed: every key that may appear in visibleColumnKeys for this scope.
 *  - defaults: keys visible in the default visible set (excludes OPT_IN_COLUMNS).
 *  - required: keys that are always visible and cannot be hidden.
 *  - Cache keys must stay compatible with the legacy Kendo shapes.
 *  - `unresolvedIssues` is in allowed only when iimEnabled is true.
 */
// ---------------------------------------------------------------------------
// All known column keys (display order — primary column first)
// ---------------------------------------------------------------------------
/** All user-controllable column keys in display order (excludes selector pseudo-column). */
export const ALL_INSPECTION_COLUMNS = [
    'id',
    'workOrderNumber',
    'referenceNumber',
    'frequency',
    'description',
    'scheduledDate',
    'inspectionDate',
    'dueDate',
    'assignedTo',
    'building',
    'account',
    'zipCode',
    'status',
    'inspectionType',
    'visits',
    'unresolvedIssues'
];
/** The `id` column is always required (primary link cell). */
const REQUIRED_COLUMNS = ['id'];
/**
 * Opt-in columns: allowed in every scope, but never in the default visible set.
 * The user turns them on from the column menu.
 *
 * `visits` is opt-in because it renders every visit date for the inspection, which is
 * a wide cell most listings do not need.
 */
const OPT_IN_COLUMNS = ['visits'];
// ---------------------------------------------------------------------------
// Column policy factory
// ---------------------------------------------------------------------------
/**
 * Returns the column policy for a given scope and iimEnabled flag.
 *
 * - `standalone`: all columns visible by default except opt-in ones (see OPT_IN_COLUMNS);
 *   unresolvedIssues conditionally allowed.
 * - `building` / `inspectionSeries`: `building` column removed from defaults.
 * - `account`: `account` column removed from defaults.
 */
export function columnPolicyForScope(scope, iimEnabled) {
    const allowed = iimEnabled
        ? [...ALL_INSPECTION_COLUMNS]
        : ALL_INSPECTION_COLUMNS.filter((c) => c !== 'unresolvedIssues');
    let defaults;
    if (scope.type === 'building' || scope.type === 'inspectionSeries') {
        defaults = allowed.filter((c) => c !== 'building');
    }
    else if (scope.type === 'account') {
        defaults = allowed.filter((c) => c !== 'account');
    }
    else {
        defaults = [...allowed];
    }
    // Opt-in columns stay out of every default set, regardless of scope.
    defaults = defaults.filter((c) => !OPT_IN_COLUMNS.includes(c));
    return {
        allowed: [...allowed],
        defaults,
        required: [...REQUIRED_COLUMNS]
    };
}
// ---------------------------------------------------------------------------
// Cache key factory
// ---------------------------------------------------------------------------
/**
 * Returns the localStorage cache key for a given scope.
 * Matches the legacy Kendo keys supplied by Rails views.
 *
 * standalone:        kendo/inspections/index
 * building:          kendo/buildings/{id}/inspections
 * account:           kendo/accounts/{id}/inspections
 * inspectionSeries:  kendo/inspection_series/{id}/inspections
 */
export function cacheKeyForScope(scope) {
    switch (scope.type) {
        case 'building':
            return `kendo/buildings/${scope.id}/inspections`;
        case 'account':
            return `kendo/accounts/${scope.id}/inspections`;
        case 'inspectionSeries':
            return `kendo/inspection_series/${scope.id}/inspections`;
        case 'standalone':
        default:
            return 'kendo/inspections/index';
    }
}
